import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { graph, palette } from '../utils/designTokens';
import {
  getNodeConfig,
  getNodeBorderStyle,
  getNodeBoxShadow,
  getSecurityBadgeConfig,
  getSecurityBadgeLeftPosition,
  getInputBadgesForNode,
  getOutputBadgesForNode
} from '../utils/graphHelpers';

const GraphNode = ({ data, selected, onBadgeToggle }) => {
  if (!data) throw new Error('GraphNode requires data prop');
  if (!data.label) throw new Error('GraphNode requires data.label');
  if (!data.type) throw new Error('GraphNode requires data.type');

  const isMainNode = data.isMainAgent || data.isRootSpan || false;
  const nodeCategory = data.nodeCategory || 'entity';
  const isDanger = data.isDanger || false;
  const isProtected = data.isProtected || false;
  const isEnhancement = data.isEnhancement || false;

  // Get node configuration from design tokens
  const nodeConfig = getNodeConfig(data.type, nodeCategory);
  const { stroke: strokeColor, icon: IconComponent, label: typeLabel } = nodeConfig;

  // Get border style (solid for entity, dashed for concept)
  const borderStyle = getNodeBorderStyle(nodeCategory);

  // Use red border for danger/error nodes, green for protected-only nodes
  // When both isDanger and isProtected, keep red border (outer green will be added via box-shadow)
  const effectiveStrokeColor = isDanger ? '#FF6B6B' : isProtected ? palette.status.success : strokeColor;

  // Check if this node has an active badge
  const hasActiveBadge = data.activeBadge?.nodeId === data.id;

  // Determine box shadow based on state
  let nodeState = 'default';
  if (isDanger && isProtected) {
    nodeState = 'dangerProtected'; // Both mutation (red) and policy protection (green outer)
  } else if (isDanger) {
    nodeState = 'danger';
  } else if (isProtected) {
    nodeState = 'protected';
  } else if (selected && hasActiveBadge) {
    nodeState = 'selectedWithBadge';
  } else if (selected) {
    nodeState = 'selected';
  }
  // Removed mainAgent state - root node should only glow when selected
  // For dangerProtected, we'll use a wrapper div instead of box-shadow
  const boxShadow = nodeState === 'dangerProtected'
    ? getNodeBoxShadow('danger', effectiveStrokeColor) // Just the danger glow on inner node
    : getNodeBoxShadow(nodeState, effectiveStrokeColor);

  // Whether to show the wrapper (policy protection or enhancement)
  // Show wrapper when: (danger + protected) OR (enhancement only)
  const showProtectionWrapper = (isDanger && isProtected) || isEnhancement;

  // Node dimensions and styling from design system
  const nodeStyle = {
    width: `${graph.nodes.dimensions.width}px`,
    height: `${graph.nodes.dimensions.height}px`,
    borderRadius: `${graph.nodes.dimensions.borderRadius}px`,
    border: `${isDanger ? 2 : graph.nodes.dimensions.borderWidth}px ${borderStyle} ${effectiveStrokeColor}`,
    backgroundColor: graph.nodes.background,
    boxShadow,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: graph.nodes.dimensions.innerGap,
    padding: graph.nodes.dimensions.padding,
    position: 'relative'
  };

  const labelStyle = {
    fontSize: graph.nodes.text.fontSize.label,
    fontWeight: graph.nodes.text.fontWeight.label,
    color: graph.nodes.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: graph.nodes.text.spacing.labelMaxWidth,
    textAlign: 'center',
    marginTop: graph.nodes.text.spacing.labelMarginTop,
    paddingLeft: graph.nodes.text.spacing.labelPaddingLeft,
    paddingRight: graph.nodes.text.spacing.labelPaddingRight
  };

  const typeStyle = {
    fontSize: graph.nodes.text.fontSize.type,
    fontWeight: graph.nodes.text.fontWeight.type,
    color: graph.nodes.text.secondary,
    opacity: graph.nodes.text.spacing.typeOpacity,
    textAlign: 'center',
    marginTop: graph.nodes.text.spacing.typeMarginTop,
    textTransform: 'uppercase'
  };

  // Get input and output detections for this node
  const inputDetections = getInputBadgesForNode(data);
  const outputDetections = getOutputBadgesForNode(data);
  const activeBadge = data.activeBadge; // { nodeId, badgeType, position } or null

  // Helper function to render a badge group
  const renderBadgeGroup = (detections, position) => {
    if (detections.length === 0) return null;

    const config = position === 'bottom'
      ? graph.badges.securityBadges.outputBadges
      : graph.badges.securityBadges.inputBadges;

    // Get highest severity across all detections at this position
    const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
    const highestSeverity = detections.reduce((highest, d) => {
      const detectionSeverity = (d.severity || 'low').toLowerCase();
      const currentLevel = severityOrder[detectionSeverity] || 1;
      const highestLevel = severityOrder[highest] || 1;
      return currentLevel > highestLevel ? detectionSeverity : highest;
    }, 'low');

    // Total count of all detections
    const totalCount = detections.length;

    // Use ShieldAlert icon for all security badges
    const { icon: BadgeIcon } = getSecurityBadgeConfig('prompt_injection');

    // Check if this security badge is active
    const isActive = activeBadge?.nodeId === data.id && activeBadge?.position === position;

    // Get state-based styling with fallback
    const stateStyle = isActive
      ? (graph.badges.securityBadges.active || { borderWidth: '2px', background: '#2C2C2C', boxShadow: '0 2px 6px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.3)' })
      : (graph.badges.securityBadges.inactive || { borderWidth: '1px', background: '#202020', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' });

    // Get border color from design system severity colors
    const borderColor = palette.severity[highestSeverity]?.text || graph.badges.securityBadges.borderColor;

    return (
      <div
        key={position}
        onClick={(e) => {
          e.stopPropagation();
          if (onBadgeToggle) {
            onBadgeToggle(data.id, 'security', position);
          }
        }}
        style={{
          position: 'absolute',
          [position === 'bottom' ? 'bottom' : 'top']: position === 'bottom' ? config.bottom : config.top,
          left: getSecurityBadgeLeftPosition(0, position),
          width: graph.badges.securityBadges.size,
          height: graph.badges.securityBadges.size,
          borderRadius: graph.badges.securityBadges.borderRadius,
          border: `${stateStyle.borderWidth} solid ${borderColor}`,
          backgroundColor: stateStyle.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: stateStyle.boxShadow,
          transition: graph.badges.securityBadges.transition
        }}
        title={`Security Detections (${totalCount} ${position === 'top' ? 'input' : 'output'}, ${highestSeverity} severity)`}
      >
        {BadgeIcon && (
          <BadgeIcon
            size={graph.badges.securityBadges.iconSize}
            color={borderColor}
            strokeWidth={1.5}
          />
        )}
      </div>
    );
  };

  // Protection wrapper style (dotted green border with gap)
  const protectionWrapperStyle = showProtectionWrapper ? {
    position: 'relative',
    padding: '4px', // Gap between inner red and outer green
    border: `2px dotted ${palette.status.success}`,
    borderRadius: `${graph.nodes.dimensions.borderRadius + 4}px`,
    backgroundColor: 'transparent'
  } : null;

  const nodeContent = (
    <div style={nodeStyle}>
      {/* Connection handles for entity nodes (vertical flow) */}
      {nodeCategory === 'entity' && (
        <>
          <Handle
            id="top"
            type="target"
            position={Position.Top}
            style={{ opacity: 0 }}
          />
          <Handle
            id="bottom"
            type="source"
            position={Position.Bottom}
            style={{ opacity: 0 }}
          />
        </>
      )}

      {/* Connection handles for concept nodes (horizontal flow from agent) */}
      {nodeCategory === 'concept' && (
        <>
          <Handle
            id="left"
            type="target"
            position={Position.Left}
            style={{ opacity: 0 }}
          />
          <Handle
            id="right"
            type="source"
            position={Position.Right}
            style={{ opacity: 0 }}
          />
          {/* Turns concept nodes need bottom handle for vertical connection from session nodes */}
          {data.type === 'turns' && (
            <Handle
              id="bottom"
              type="target"
              position={Position.Bottom}
              style={{ opacity: 0 }}
            />
          )}
        </>
      )}

      {/* Icon (top left corner) */}
      {IconComponent && (
        <div style={{ position: 'absolute', top: graph.nodes.icon.top, left: graph.nodes.icon.left, fontSize: graph.nodes.icon.fontSize }}>
          <IconComponent size={graph.nodes.icon.size} color={effectiveStrokeColor} strokeWidth={graph.nodes.icon.strokeWidth} />
        </div>
      )}

      {/* Label */}
      <div style={labelStyle} title={data.label}>
        {data.label}
      </div>

      {/* Type label */}
      <div style={typeStyle}>
        {data.type.toUpperCase()}
      </div>

      {/* Input security badges (top - detections on system/user messages) */}
      {renderBadgeGroup(inputDetections, 'top')}

      {/* Output security badges (bottom - detections on assistant messages) */}
      {renderBadgeGroup(outputDetections, 'bottom')}
    </div>
  );

  // Wrap in protection/enhancement wrapper
  if (showProtectionWrapper) {
    return (
      <div style={protectionWrapperStyle}>
        {/* Label above the dotted border */}
        <div style={{
          position: 'absolute',
          top: '-10px',
          right: '8px',
          backgroundColor: graph.nodes.background,
          padding: '0 4px',
          fontSize: '9px',
          fontWeight: 600,
          color: palette.status.success,
          letterSpacing: '0.5px',
          display: 'flex',
          alignItems: 'center',
          gap: '3px'
        }}>
          {isEnhancement ? '✨ ADDED' : '🛡 POLICY'}
        </div>
        {nodeContent}
      </div>
    );
  }

  return nodeContent;
};

export default memo(GraphNode);
